import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Container,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import MetaTags from "react-meta-tags";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AxiosResponse } from "axios";

import Breadcrumbs from "../../components/common/Breadcrumb";
import { ApiCore } from "../../helpers/api_helper";
import { Loading } from "../../components/common/Loading";
import { Endpoints } from "../../utils/Endpoints";

const request = new ApiCore();

const normalizePhone = (value: string): string => {
  if (!value) return "";
  let digits = value.replace(/\D+/g, "");
  if (digits.startsWith("256")) {
    digits = digits.slice(3);
  }
  digits = digits.replace(/^0+/, "");
  if (!digits) return "";
  return `256${digits}`;
};

const ProfileSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string()
    .nullable()
    .test(
      "valid-phone",
      "Phone must start with 256 and contain digits only",
      (value) => {
        if (!value) return true;
        const normalized = normalizePhone(value);
        return /^256\d{6,12}$/.test(normalized);
      }
    ),
});

const Profile = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [profile, setProfile] = useState<any>({
    name: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    request
      .get(Endpoints.userProfile, {})
      .then((response: AxiosResponse) => {
        const data = response.data || {};
        setProfile({
          name: data.name || "",
          email: data.email || "",
          phone: normalizePhone(data.phone || ""),
        });
      })
      .catch((error) => {
        console.error(error);
        const stored = localStorage.getItem("__portal__user__");
        if (stored) {
          try {
            const user = JSON.parse(stored);
            setProfile({
              name: user.name || "",
              email: user.email || "",
              phone: normalizePhone(user.phone || ""),
            });
          } catch (e) {
            console.error(e);
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleSubmit = (values: any) => {
    const normalizedPhone = normalizePhone(values.phone);
    const payload = {
      user: {
        name: values.name,
        email: values.email,
        phone: normalizedPhone,
      },
    };

    toast
      .promise(request.update(Endpoints.userProfile, payload), {
        pending: "Updating profile...",
        success: "Profile updated",
        error: {
          render({ data }) {
            return typeof data === "string" ? data : "Could not update profile";
          },
        },
      })
      .then((response: AxiosResponse) => {
        const data = response.data || {};
        const merged = {
          name: data.name ?? values.name,
          email: data.email ?? values.email,
          phone: normalizePhone(data.phone ?? normalizedPhone),
        };
        setProfile(merged);

        const stored = localStorage.getItem("__portal__user__");
        if (stored) {
          try {
            const user = JSON.parse(stored);
            localStorage.setItem(
              "__portal__user__",
              JSON.stringify({ ...user, ...merged })
            );
          } catch (e) {
            console.error(e);
          }
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>My Profile</title>
        </MetaTags>

        <Container fluid>
          <Breadcrumbs title="Account" breadcrumbItem="My Profile" />
          <ToastContainer />

          <Row>
            <Col md={8} lg={6}>
              <Card>
                <CardHeader>
                  <CardTitle className="h5 mb-0">My Profile</CardTitle>
                </CardHeader>
                <CardBody>
                  {loading && <Loading label="Loading profile..." />}
                  {!loading && (
                    <Formik
                      initialValues={profile}
                      enableReinitialize
                      validationSchema={ProfileSchema}
                      onSubmit={handleSubmit}
                    >
                      {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
                        <Form>
                          <FormGroup className="mb-3">
                            <Label htmlFor="name">Name</Label>
                            <Input
                              id="name"
                              name="name"
                              type="text"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={!!(touched.name && errors.name)}
                            />
                            {touched.name && errors.name && (
                              <div className="invalid-feedback d-block">
                                {String(errors.name)}
                              </div>
                            )}
                          </FormGroup>

                          <FormGroup className="mb-3">
                            <Label htmlFor="email">Email</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={values.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={!!(touched.email && errors.email)}
                            />
                            {touched.email && errors.email && (
                              <div className="invalid-feedback d-block">
                                {String(errors.email)}
                              </div>
                            )}
                          </FormGroup>

                          <FormGroup className="mb-3">
                            <Label htmlFor="phone">Phone</Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="text"
                              placeholder="256700000000"
                              value={values.phone}
                              onChange={handleChange}
                              onBlur={(e) => {
                                handleBlur(e);
                                const normalized = normalizePhone(e.target.value);
                                if (normalized !== e.target.value) {
                                  setFieldValue("phone", normalized);
                                }
                              }}
                              invalid={!!(touched.phone && errors.phone)}
                            />
                            <small className="text-muted">
                              Must start with 256, no leading zero or spaces.
                            </small>
                            {touched.phone && errors.phone && (
                              <div className="invalid-feedback d-block">
                                {String(errors.phone)}
                              </div>
                            )}
                          </FormGroup>

                          <Button
                            type="submit"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            Save changes
                          </Button>
                        </Form>
                      )}
                    </Formik>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Profile;
